import React from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Image,
  Tag,
  Container,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { MdLocalOffer } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import SeeMoreButton from "../../Common/SeeMoreButton";
import { useTeamActivity } from "../../../hooks/useTeamActivity";

const JoinUsTeamActivity = () => {
  const navigate = useNavigate();
  const { teamActivityPosts, loading, error } = useTeamActivity(1, 1);

  // Get only first 2 activities for this component
  const activities = teamActivityPosts.slice(0, 2);

  const handleNavigateToTeamActivity = () => {
    navigate("/team-activity");
  };

  // Handle loading state
  if (loading) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Heading
          as="h2"
          fontSize={{ base: "3xl", md: "4xl" }}
          color="#25575F"
          mb={10}
          textAlign="center"
          fontWeight="bold"
        >
          Team Activity
        </Heading>
        <Flex justifyContent="center" alignItems="center" minH="200px">
          <Spinner size="xl" color="#25575F" />
        </Flex>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Heading
          as="h2"
          fontSize={{ base: "3xl", md: "4xl" }}
          color="#25575F"
          mb={10}
          textAlign="center"
          fontWeight="bold"
        >
          Team Activity
        </Heading>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text>{error}</Text>
        </Alert>
      </Container>
    );
  }

  // Handle empty data
  if (!activities || activities.length === 0) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Heading
          as="h2"
          fontSize={{ base: "3xl", md: "4xl" }}
          color="#25575F"
          mb={10}
          textAlign="center"
          fontWeight="bold"
        >
          Team Activity
        </Heading>
        <Text textAlign="center" color="gray.500">
          No team activities available at the moment.
        </Text>
      </Container>
    );
  }

  const handleActivityClick = (activity, index) => {
    // Use activity ID or slug if available, otherwise fall back to index
    const activityId = activity.id || activity.slug || index + 1;
    
    navigate(`/team-activity/${activityId}`, {
      state: { teamActivity: activity },
    });
  };

  return (
    <Container maxW="7xl" py={{ base: 8, md: 16 }}>
      <Heading
        as="h2"
        fontSize={{ base: "3xl", md: "4xl" }}
        color="#25575F"
        mb={10}
        textAlign="center"
        fontWeight="bold"
      >
        Team Activity
      </Heading>
      <Flex direction="column" gap={10}>
        {activities.map((item, idx) => (
          <Flex
            key={`activity-${item.id || idx}`}
            bg="transparent"
            borderRadius="20px"
            boxShadow="sm"
            border="1px solid #d1d5db"
            p={{ base: 4, md: 8 }}
            align="center"
            gap={8}
            flexDirection={{ base: "column", md: "row" }}
            cursor="pointer"
            onClick={() => handleActivityClick(item, idx)}
            _hover={{
              boxShadow: "md",
              transform: "translateY(-2px)",
              transition: "all 0.2s ease-in-out",
            }}
          >
            <Box position="relative" flexShrink={0}>
              {item.isVideo && item.videoUrl ? (
                <>
                  <video
                    src={item.videoUrl}
                    style={{
                      borderRadius: '16px',
                      width: '220px',
                      height: '220px',
                      objectFit: 'cover'
                    }}
                    muted
                    autoPlay
                    loop
                  />
                  <Box
                    position="absolute"
                    top={2}
                    right={2}
                    bg="rgba(0, 0, 0, 0.7)"
                    color="white"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    VIDEO
                  </Box>
                </>
              ) : (
                <Image
                  src={item.image}
                  alt={item.title || "Team Activity"}
                  borderRadius="16px"
                  boxSize={{ base: "220px", md: "220px" }}
                  objectFit="cover"
                  fallback={<Box boxSize={{ base: "220px", md: "220px" }} bg="gray.200" borderRadius="16px" />}
                />
              )}
            </Box>
            <Box flex={1} minW={0}>
              <Flex align="center" mb={2} gap={2}>
                <Box as={MdLocalOffer} color="#25575F" />
                <Tag
                  color="#25575F"
                  bg="#F8F9F6"
                  fontWeight="medium"
                  fontSize="md"
                >
                  {typeof item.tag === 'object' ? 
                    (item.tag?.fullName || item.tag?.name || "News") : 
                    (item.tag || "News")}
                </Tag>
              </Flex>
              <Heading
                as="h3"
                fontSize={{ base: "xl", md: "2xl" }}
                color="#222"
                fontWeight="semibold"
                mb={2}
              >
                {item.title}
              </Heading>
              <Text color="gray.600" fontSize={{ base: "md", md: "lg" }}>
                {item.desc || item.description || "No description available."}
              </Text>
            </Box>
          </Flex>
        ))}
      </Flex>
      {teamActivityPosts.length > 2 && (
        <SeeMoreButton
          onClick={handleNavigateToTeamActivity}
          colors={{
            textColor: "#25575F",
            textBorder: "1px solid #25575F",
            textHover: "#f0f9fa",
            iconBg: "#215560",
            iconHover: "#1a3d17",
          }}
        />
      )}
    </Container>
  );
};

export default JoinUsTeamActivity;
